import { TFunction } from 'i18next';

import { url } from '../core/utils/link.ts';

interface IRecordListPathOptions {
    channelId?: string | null;
    finished: boolean;
    testing: boolean;
}

export const getRecordListPath = (
    documentConfiguration: any,
    { channelId, finished, testing }: IRecordListPathOptions
): string => {
    if (channelId) {
        const channelPath = finished
            ? documentConfiguration.CHANNEL_FINISHED_PATH
            : testing
              ? documentConfiguration.CHANNEL_TESTING_PATH
              : documentConfiguration.CHANNEL_PATH;
        return url(channelPath, { channelId });
    }

    return finished
        ? documentConfiguration.OVERVIEW_FINISHED_PATH
        : testing
          ? documentConfiguration.OVERVIEW_TESTING_PATH
          : documentConfiguration.OVERVIEW_PATH;
};

export const isRecordInAssistableStatus = (status: string): boolean =>
    ['ASSISTANCE_NEEDED', 'ON_HOLD'].includes(status);

export const isRecordInDiscardableStatus = (status: string): boolean =>
    isRecordInAssistableStatus(status) || ['REJECTED', 'FAILED'].includes(status);

/**
 * Resolves a status display label using product-scoped i18n keys with fallback to shared keys.
 *
 * Resolution order:
 * 1. `${documentTypeName}.overview.filters.status.options.${status}` (product-specific)
 * 2. `overview.filters.status.options.${status}` (shared fallback)
 */
export const getStatusLabel = (t: TFunction, status: string, documentTypeName: string): string => {
    const sharedKey = `overview.filters.status.options.${status}`;
    const productKey = `${documentTypeName}.${sharedKey}`;
    return t(productKey, { defaultValue: t(sharedKey) });
};

/**
 * Filters and returns a list of attachments with the following logic:
 * - If the file is the processed (source) file, it is added first regardless of attachment type.
 * - All other files are included only if their contentDisposition is 'attachment'.
 * Each returned item indicates whether it is the source file.
 */
export const filterAttachments = (record: any): { isSource: boolean; file: any }[] => {
    let attachments = record?.sourceFile?.attachments ?? [];
    // If the sourcefile had no attachments, we check if the sourcefile itself is a pdf and add it as an attachment itself
    if (attachments.length === 0 && record?.sourceFile?.type === 'application/pdf') {
        attachments = [record.sourceFile];
    }

    const possibleSource = attachments.find((attachment) => attachment.name === record?.inputFile?.name);

    const isAcceptedAttachment = (file: any) =>
        file.type === 'application/pdf' || file.contentDisposition === 'attachment' || !file.cid;

    const result: { isSource: boolean; file: any }[] = [];

    attachments.forEach((file) => {
        if (file === possibleSource) {
            result.unshift({ isSource: true, file });
        } else if (isAcceptedAttachment(file)) {
            result.push({ isSource: false, file });
        }
    });

    return result;
};
